import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Radio, Space, Typography } from 'antd';
import { Eye, Pencil, Save, ScrollText, Sparkles } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  type ReportBlock,
  type OverallScheme,
  getOverallAssessmentItems,
  parseOverallSelections,
  applyOverallSelections,
} from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import { normalizeReportContent } from './report-content';
import { MarkdownView } from './MarkdownView';
import { BlockEditor } from './BlockEditor';
import { AiLogDrawer } from './AiLogDrawer';

/** AuditReportEditor 元件 Props */
interface Props {
  /** 報告內容（v1 舊格式或 v2 板塊化格式） */
  content: unknown;
  /** 所屬案件 ID（編輯態 API 呼叫使用） */
  caseId: number;
  /** 報告版本 ID（編輯態 API 呼叫使用） */
  versionId: number;
  /** 驗證方案，決定總體評價逐項條目（序168 TOP / 序169-170 TAP；缺省 TOP） */
  scheme?: OverallScheme;
  /** 是否可編輯（草稿態 true；唯讀態 false） */
  editable: boolean;
  /** 儲存完成後的回呼（如讓父層刷新資料） */
  onSaved?: () => void;
}

/**
 * 稽核報告板塊化檢視／編輯器。
 *
 * - 讀態（editable=false）：逐塊顯示 Markdown 卡片。
 * - 編輯態（editable=true，v2 格式）：每塊可切換至 TipTap WYSIWYG 編輯；
 *   AI/mixed 塊顯示「AI 重新生成」按鈕；底部「儲存報告」按鈕 PUT 全部板塊。
 * - 舊版 v1 格式：強制唯讀，顯示提示標語。
 * - 無板塊時顯示空狀態提示。
 */
export function AuditReportEditor({
  content,
  caseId,
  versionId,
  scheme = 'TOP',
  editable,
  onSaved,
}: Props) {
  /** 總體評價逐項條目（依方案） */
  const overallItems = useMemo(() => getOverallAssessmentItems(scheme), [scheme]);
  /** 正規化後的板塊清單與是否為舊版旗標 */
  const { blocks: initialBlocks, legacy } = useMemo(
    () => normalizeReportContent(content),
    [content],
  );

  /** 本地可編輯板塊清單（含使用者修改） */
  const [blocks, setBlocks] = useState<ReportBlock[]>(initialBlocks);

  /** 目前正在編輯的板塊 key（同時只編輯一塊） */
  const [editingKey, setEditingKey] = useState<string | null>(null);

  /** AI 生成日誌抽屜顯示狀態（讀/編輯態均可開啟） */
  const [logOpen, setLogOpen] = useState(false);

  /**
   * 當 content prop 變更時（父層 invalidate() 觸發 refetch 後），
   * 重新同步 blocks 狀態以反映最新資料庫內容。
   *
   * 不會造成渲染迴圈：
   *  - `content` 僅在 TanStack Query refetch 後才獲得新物件參考。
   *  - 此 query 沒有背景輪詢（無 refetchInterval），只有明確的 invalidate()。
   *  - 因此 useEffect 僅在使用者主動儲存或重新生成後才觸發一次，不會循環。
   */
  useEffect(() => {
    setBlocks(normalizeReportContent(content).blocks);
    setEditingKey(null);
  }, [content]);

  // ── 儲存整份報告 ──────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put(`/api/cases/${caseId}/audit-report/versions/${versionId}`, { blocks }),
    onSuccess: () => {
      void message.success('報告已儲存');
      onSaved?.();
    },
    onError: (e: Error) => void message.error(e.message || '儲存失敗'),
  });

  // ── 單塊 AI 重新生成 ───────────────────────────────────────────
  const regenMutation = useMutation({
    mutationFn: (key: string) =>
      apiClient.post(
        `/api/cases/${caseId}/audit-report/versions/${versionId}/blocks/${key}/regenerate`,
        {},
      ),
    onSuccess: () => {
      void message.success('板塊已重新生成');
      onSaved?.();
    },
    onError: (e: Error) => void message.error(e.message || '重新生成失敗'),
  });

  /** 更新指定板塊的 Markdown 內容並標記為「使用者已編輯」 */
  const setBlockMarkdown = (key: string, md: string) =>
    setBlocks((prev) =>
      prev.map((b) => (b.key === key ? { ...b, markdown: md, editedByUser: true } : b)),
    );

  // ── 空狀態 ────────────────────────────────────────────────────
  if (blocks.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<Typography.Text type="secondary">尚無報告內容</Typography.Text>}
      />
    );
  }

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {/* 工具列：AI 生成日誌入口（讀/編輯態均可查看，不受 editable 限制） */}
      <div>
        <Button size="small" icon={<ScrollText size={14} />} onClick={() => setLogOpen(true)}>
          AI 生成日誌
        </Button>
        <AiLogDrawer caseId={caseId} open={logOpen} onClose={() => setLogOpen(false)} />
      </div>

      {/* 舊版 v1 格式提示 */}
      {legacy && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          舊版報告（相容檢視，唯讀）
        </Typography.Text>
      )}

      {/* 逐塊渲染 */}
      {blocks.map((b) => {
        const isEditing = editable && !legacy && editingKey === b.key;

        return (
          <Card
            key={b.key}
            size="small"
            type="inner"
            title={b.title}
            extra={
              editable && !legacy ? (
                <Space>
                  {/* AI / mixed 板塊：顯示「AI 重新生成」按鈕 */}
                  {b.kind !== 'data' && (
                    <Button
                      size="small"
                      icon={<Sparkles size={14} />}
                      loading={regenMutation.isPending && regenMutation.variables === b.key}
                      onClick={() => regenMutation.mutate(b.key)}
                    >
                      AI 重新生成
                    </Button>
                  )}
                  {/* 編輯／完成切換 */}
                  <Button
                    size="small"
                    type="text"
                    icon={isEditing ? <Eye size={14} /> : <Pencil size={14} />}
                    onClick={() => setEditingKey(isEditing ? null : b.key)}
                  >
                    {isEditing ? '完成' : '編輯'}
                  </Button>
                </Space>
              ) : undefined
            }
          >
            {isEditing ? (
              /* 編輯態：TipTap WYSIWYG（總體評價塊另附勾選項，序83） */
              <>
                {b.key === 'overallAssessment' &&
                  (() => {
                    // 逐項勾選（序168 TOP / 序169-170 TAP）：系統默認條目 + 人工勾選 + 人工填寫。
                    // 狀態以哨兵區形式序列化進 markdown（單一真源，round-trip 穩定）。
                    const selections = parseOverallSelections(b.markdown, overallItems);
                    const setSel = (key: string, patch: { value?: string; note?: string }) => {
                      const next = {
                        ...selections,
                        [key]: { ...(selections[key] ?? {}), ...patch },
                      };
                      setBlockMarkdown(
                        b.key,
                        applyOverallSelections(b.markdown, overallItems, next),
                      );
                    };
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          總體評價逐項勾選（依驗證方案，勾選後寫入內文開頭）：
                        </Typography.Text>
                        <Space
                          direction="vertical"
                          size={10}
                          style={{ display: 'flex', marginTop: 8 }}
                        >
                          {overallItems.map((item) => {
                            const sel = selections[item.key] ?? {};
                            return (
                              <div key={item.key}>
                                <Typography.Paragraph
                                  style={{ marginBottom: 4, whiteSpace: 'pre-line', fontSize: 13 }}
                                >
                                  {item.label}
                                </Typography.Paragraph>
                                <Radio.Group
                                  value={sel.value}
                                  onChange={(e) => setSel(item.key, { value: e.target.value })}
                                >
                                  {item.options.map((o) => (
                                    <Radio key={o} value={o}>
                                      {o}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                                {item.hasNote && (
                                  <Input
                                    style={{ marginTop: 4 }}
                                    placeholder="說明"
                                    value={sel.note ?? ''}
                                    onChange={(e) => setSel(item.key, { note: e.target.value })}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </Space>
                      </div>
                    );
                  })()}
                <BlockEditor value={b.markdown} onChange={(md) => setBlockMarkdown(b.key, md)} />
              </>
            ) : (
              /* 讀態：靜態 Markdown 渲染 */
              <MarkdownView markdown={b.markdown} />
            )}
          </Card>
        );
      })}

      {/* 編輯態底部儲存按鈕（僅 v2 格式可儲存） */}
      {editable && !legacy && (
        <Button
          type="primary"
          icon={<Save size={14} />}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          儲存報告
        </Button>
      )}
    </Space>
  );
}
